import { Controller } from "@hotwired/stimulus";

import {
  DEFAULT_FRAME_TIMEOUT_MS,
  armFrameLoadTimeout,
} from "./frame_load_timeout";

// Same card URL is mounted once per CSS breakpoint shell. Cache HTML by src so
// tablet/mobile frames reuse the first successful load instead of refetching.
// Cloned shells get ids prefixed with their frame id so label and ARIA refs stay unique.
// ponytail: bounded module cache; upgrade path is LRU store if card count grows
const MAX_LOADED_HTML_CACHE_SIZE = 24;
const LOADED_HTML_CACHE_TTL_MS = 5 * 60 * 1000;
const loadedHtmlBySrc = new Map();

function readCachedEntry(url) {
  const entry = loadedHtmlBySrc.get(url);
  if (!entry) return null;

  if (Date.now() - entry.storedAt > LOADED_HTML_CACHE_TTL_MS) {
    loadedHtmlBySrc.delete(url);
    return null;
  }

  entry.storedAt = Date.now();
  return entry;
}

function readCachedHtml(url) {
  return readCachedEntry(url)?.html ?? null;
}

function writeCachedHtml(url, html, sourceFrameId) {
  loadedHtmlBySrc.set(url, { html, sourceFrameId, storedAt: Date.now() });
  evictLoadedHtmlCache();
}

const ID_REFERENCE_ATTRIBUTES = new Set([
  "for",
  "headers",
  "list",
  "form",
  "aria-activedescendant",
  "aria-controls",
  "aria-describedby",
  "aria-details",
  "aria-errormessage",
  "aria-flowto",
  "aria-labelledby",
  "aria-owns",
  "data-turbo-frame",
]);

function attributeReferencesIds(name) {
  return (
    ID_REFERENCE_ATTRIBUTES.has(name) ||
    name.endsWith("frame-id-value") ||
    name.endsWith("frame-ids-value")
  );
}

function collectElements(root) {
  const elements = [];
  const visit = (node) => {
    node.querySelectorAll("*").forEach((element) => {
      elements.push(element);
      if (element.tagName === "TEMPLATE") visit(element.content);
    });
  };
  visit(root);
  return elements;
}

function rewriteIdRefs(value, idMap) {
  const trimmed = value.trim();
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return JSON.stringify(
          parsed.map((item) =>
            typeof item === "string" && idMap[item] ? idMap[item] : item,
          ),
        );
      }
    } catch {
      // not JSON; fall through to token replace
    }
  }

  if (idMap[value]) return idMap[value];
  if (!value.includes(" ")) return value;

  return value
    .split(/\s+/)
    .map((token) => idMap[token] || token)
    .join(" ");
}

// ponytail: only label, ARIA, turbo-frame, and *frame-id(s)-value attributes are
// rewritten. Upgrade path: add the attribute to ID_REFERENCE_ATTRIBUTES.
export function scopeDuplicateIds(html, { sourceFrameId, destFrameId, alias = {} }) {
  if (!html || !destFrameId || sourceFrameId === destFrameId) return html;

  const container = document.createElement("div");
  container.innerHTML = html;
  const idMap = { ...alias };
  if (sourceFrameId) idMap[sourceFrameId] = destFrameId;

  const elements = collectElements(container);
  elements.forEach((element) => {
    const id = element.getAttribute("id");
    if (!id || idMap[id]) return;
    if (id === destFrameId || id.startsWith(`${destFrameId}__`)) return;
    idMap[id] = `${destFrameId}__${id}`;
  });

  elements.forEach((element) => {
    const id = element.getAttribute("id");
    if (id && idMap[id]) element.setAttribute("id", idMap[id]);

    [...element.attributes].forEach((attribute) => {
      if (attribute.name === "id") return;

      if (attribute.name === "href" && attribute.value.startsWith("#")) {
        const target = attribute.value.slice(1);
        if (idMap[target]) element.setAttribute("href", `#${idMap[target]}`);
        return;
      }

      if (!attributeReferencesIds(attribute.name)) return;

      const next = rewriteIdRefs(attribute.value, idMap);
      if (next !== attribute.value) element.setAttribute(attribute.name, next);
    });
  });

  return container.innerHTML;
}

function evictLoadedHtmlCache() {
  const now = Date.now();

  for (const [url, entry] of loadedHtmlBySrc) {
    if (now - entry.storedAt > LOADED_HTML_CACHE_TTL_MS) {
      loadedHtmlBySrc.delete(url);
    }
  }

  while (loadedHtmlBySrc.size > MAX_LOADED_HTML_CACHE_SIZE) {
    let oldestUrl = null;
    let oldestAt = Infinity;

    for (const [url, entry] of loadedHtmlBySrc) {
      if (entry.storedAt < oldestAt) {
        oldestAt = entry.storedAt;
        oldestUrl = url;
      }
    }

    if (oldestUrl == null) break;
    loadedHtmlBySrc.delete(oldestUrl);
  }
}

export default class extends Controller {
  static targets = ["frame", "errorTemplate"];

  static values = {
    src: String,
    timeoutMs: { type: Number, default: DEFAULT_FRAME_TIMEOUT_MS },
  };

  static clearLoadedHtmlCache() {
    loadedHtmlBySrc.clear();
  }

  connect() {
    this.initialSkeletonHtml = this.frameTarget.innerHTML;
    this.clearLoadTimeout = null;
    this.loadGeneration = 0;
    this.awaitingLoad = false;
    this.boundOnLoad = this.onLoad.bind(this);
    this.boundOnMissing = this.onMissing.bind(this);
    this.boundOnFetchError = this.onFetchError.bind(this);
    this.boundOnBeforeFetchRequest = this.onBeforeFetchRequest.bind(this);
    this.boundOnRetry = this.onRetry.bind(this);
    this.boundBeforeFrameRender = this.onBeforeFrameRender.bind(this);

    this.element.addEventListener(
      "turbo:before-frame-render",
      this.boundBeforeFrameRender,
    );
    this.frameTarget.addEventListener(
      "turbo:frame-missing",
      this.boundOnMissing,
    );
    this.frameTarget.addEventListener(
      "turbo:fetch-request-error",
      this.boundOnFetchError,
    );
    this.element.addEventListener("turbo:frame-load", this.boundOnLoad);
    this.frameTarget.addEventListener(
      "turbo:before-fetch-request",
      this.boundOnBeforeFetchRequest,
    );
    this.element.addEventListener("click", this.boundOnRetry);

    const frame = this.frameTarget;

    if (this.applyCachedHtmlIfPresent()) return;

    const skeletonLoading =
      frame.getAttribute("aria-busy") === "true" ||
      (frame.innerHTML === this.initialSkeletonHtml &&
        this.initialSkeletonHtml.length > 0);

    if (frame.hasAttribute("src") || skeletonLoading) {
      this.awaitingLoad = true;
      frame.setAttribute("aria-busy", "true");
      this.loadGeneration += 1;
      frame.dataset.loadGeneration = String(this.loadGeneration);
    } else {
      this.syncHiddenFrameLayout();
    }
  }

  disconnect() {
    this.cancelLoadTimeout();
    this.frameTarget.removeEventListener(
      "turbo:frame-missing",
      this.boundOnMissing,
    );
    this.frameTarget.removeEventListener(
      "turbo:fetch-request-error",
      this.boundOnFetchError,
    );
    this.element.removeEventListener("turbo:frame-load", this.boundOnLoad);
    this.frameTarget.removeEventListener(
      "turbo:before-fetch-request",
      this.boundOnBeforeFetchRequest,
    );
    this.element.removeEventListener("click", this.boundOnRetry);
    this.element.removeEventListener(
      "turbo:before-frame-render",
      this.boundBeforeFrameRender,
    );
  }

  onBeforeFrameRender(event) {
    const newFrame = event.detail?.newFrame;
    const destFrameId = this.frameTarget?.id;
    const sourceFrameId = this.srcValue
      ? readCachedEntry(this.srcValue)?.sourceFrameId
      : null;
    if (!newFrame || !destFrameId || !sourceFrameId || sourceFrameId === destFrameId) {
      return;
    }
    if (event.target !== this.frameTarget && !this.frameTarget.contains(event.target)) {
      return;
    }

    const alias = {};
    const prefix = `${destFrameId}__`;
    if (event.target.id?.startsWith(prefix)) {
      alias[event.target.id.slice(prefix.length)] = event.target.id;
    }

    newFrame.innerHTML = scopeDuplicateIds(newFrame.innerHTML, {
      sourceFrameId,
      destFrameId,
      alias,
    });
  }

  onBeforeFetchRequest(event) {
    if (event.target !== this.frameTarget) return;

    if (
      this.requestUrlMatchesSrc(event.detail?.url) &&
      this.applyCachedHtmlIfPresent()
    ) {
      event.preventDefault();
      return;
    }

    this.awaitingLoad = true;
    this.loadGeneration += 1;
    this.frameTarget.dataset.loadGeneration = String(this.loadGeneration);
    this.frameTarget.setAttribute("aria-busy", "true");
    this.armTimeout();
  }

  armTimeout() {
    this.cancelLoadTimeout();
    if (!this.isLoading()) return;

    this.clearLoadTimeout = armFrameLoadTimeout({
      timeoutMs: this.timeoutMsValue,
      onTimeout: () => this.showError(),
    });
  }

  isLoading() {
    const frame = this.frameTarget;
    return this.awaitingLoad || frame.getAttribute("aria-busy") === "true";
  }

  onLoad(event) {
    const frame = event.target;
    if (!this.element.contains(frame)) return;

    const generation = Number(frame.dataset.loadGeneration);
    if (generation && generation !== this.loadGeneration) return;

    this.awaitingLoad = false;
    this.cancelLoadTimeout();
    frame.setAttribute("aria-busy", "false");
    this.rememberLoadedHtml();
    this.hydrateSiblingFrames();
    this.syncHiddenFrameLayout();
  }

  onMissing(event) {
    if (event.target !== this.frameTarget) return;

    event.preventDefault();
    this.showError();
  }

  async onFetchError(event) {
    if (event.target !== this.frameTarget) return;

    event.preventDefault();

    const applied = await this.applyServerFrameError(event);
    if (!applied) this.showError();
  }

  async applyServerFrameError(event) {
    const response = event.detail?.fetchResponse?.response;
    if (!response || typeof response.text !== "function") return false;

    const generation = this.loadGeneration;

    try {
      const html = await response.text();
      if (!this.element.isConnected) return false;
      if (generation !== this.loadGeneration) return false;

      const frameId = this.frameTarget.id;
      if (!html || !frameId) return false;

      const serverFrame = new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById(frameId);
      if (!serverFrame) return false;

      this.finishErrorState();
      this.frameTarget.innerHTML = serverFrame.innerHTML;
      this.focusRetryButton();
      return true;
    } catch {
      return false;
    }
  }

  showError() {
    this.finishErrorState();

    if (this.hasErrorTemplateTarget) {
      this.frameTarget.innerHTML = this.errorTemplateTarget.innerHTML;
    }

    this.focusRetryButton();
  }

  finishErrorState() {
    this.awaitingLoad = false;
    this.loadGeneration += 1;
    this.cancelLoadTimeout();

    const frame = this.frameTarget;
    frame.setAttribute("aria-busy", "false");
    frame.removeAttribute("src");
    if (this.srcValue) loadedHtmlBySrc.delete(this.srcValue);
  }

  focusRetryButton() {
    const retry = this.frameTarget.querySelector(
      "[data-educator-home-lazy-frame-retry]",
    );
    retry?.focus();
  }

  onRetry(event) {
    const retry = event.target.closest("[data-educator-home-lazy-frame-retry]");
    if (!retry || !this.element.contains(retry)) return;

    event.preventDefault();
    this.reload();
  }

  reload() {
    const url = this.srcValue;
    if (!url) return;

    loadedHtmlBySrc.delete(url);

    this.awaitingLoad = true;
    this.loadGeneration += 1;

    const frame = this.frameTarget;
    frame.dataset.loadGeneration = String(this.loadGeneration);
    frame.setAttribute("aria-busy", "true");
    this.element.classList.remove("hidden");
    this.rowChild()?.classList.remove("hidden");
    this.syncAssessmentSummaryRowLayout();
    frame.innerHTML = this.initialSkeletonHtml;
    frame.removeAttribute("src");
    frame.setAttribute("src", url);
    this.armTimeout();
  }

  rememberLoadedHtml() {
    const url = this.srcValue;
    if (!url || readCachedHtml(url) != null) return;

    writeCachedHtml(url, this.frameTarget.innerHTML, this.frameTarget.id);
  }

  requestUrlMatchesSrc(requestUrl) {
    const src = this.srcValue;
    if (!src || !requestUrl) return false;

    const srcUrl = new URL(src, window.location.origin);
    const normalizedRequest =
      requestUrl instanceof URL
        ? requestUrl
        : new URL(String(requestUrl), window.location.origin);

    return srcUrl.href === normalizedRequest.href;
  }

  applyCachedHtmlIfPresent() {
    const url = this.srcValue;
    const entry = url ? readCachedEntry(url) : null;
    if (!entry) return false;

    this.applyLoadedHtml(entry.html, entry.sourceFrameId);
    return true;
  }

  applyLoadedHtml(html, sourceFrameId = null) {
    this.awaitingLoad = false;
    this.cancelLoadTimeout();
    this.loadGeneration += 1;

    const frame = this.frameTarget;
    const destFrameId = frame.id;
    const rendered =
      sourceFrameId && destFrameId && sourceFrameId !== destFrameId
        ? scopeDuplicateIds(html, { sourceFrameId, destFrameId })
        : html;
    frame.dataset.loadGeneration = String(this.loadGeneration);
    frame.removeAttribute("src");
    frame.innerHTML = rendered;
    frame.setAttribute("aria-busy", "false");
    this.syncHiddenFrameLayout();
  }

  hydrateSiblingFrames() {
    const url = this.srcValue;
    const entry = url ? readCachedEntry(url) : null;
    if (!url || !entry) return;

    document
      .querySelectorAll("[data-controller~='educator-home-page--lazy-frame']")
      .forEach((element) => {
        if (element === this.element) return;

        const sibling = this.application.getControllerForElementAndIdentifier(
          element,
          "educator-home-page--lazy-frame",
        );
        if (!sibling || sibling.srcValue !== url) return;
        if (!sibling.frameTarget.hasAttribute("src") && !sibling.awaitingLoad)
          return;

        sibling.awaitingLoad = false;
        sibling.cancelLoadTimeout();
        sibling.loadGeneration += 1;
        sibling.frameTarget.dataset.loadGeneration = String(
          sibling.loadGeneration,
        );
        sibling.applyLoadedHtml(entry.html, entry.sourceFrameId);
      });
  }

  cancelLoadTimeout() {
    if (this.clearLoadTimeout) {
      this.clearLoadTimeout();
      this.clearLoadTimeout = null;
    }
  }

  syncHiddenFrameLayout() {
    const frame =
      this.element.querySelector(
        '[data-educator-home-page--lazy-frame-target="frame"]',
      ) ?? this.frameTarget;
    const hidden =
      frame.childElementCount === 0 && frame.textContent.trim() === "";

    this.element.classList.toggle("hidden", hidden);
    this.rowChild()?.classList.toggle("hidden", hidden);
    this.syncAssessmentSummaryRowLayout();
  }

  rowChild() {
    const row = this.element.closest(
      ".educator-home-page-assessment-summary-cards-row",
    );
    if (!row || row === this.element) return null;

    return this.element.parentElement === row
      ? this.element
      : this.element.parentElement;
  }

  syncAssessmentSummaryRowLayout() {
    const row = this.element.closest(
      ".educator-home-page-assessment-summary-cards-row",
    );
    if (!row) return;

    const rowController = this.application.getControllerForElementAndIdentifier(
      row,
      "educator-home-page--assessment-summary-cards-row",
    );
    rowController?.syncLayout();
  }
}
